<script>
import {
  EndUserTicketCategoryStatusEnum,
  ThreadStatusEnum,
  TicketCategoryStatusEnum,
} from "@/utils/constants.js";

export const threadStatusThemeMap = Object.freeze({
  [ThreadStatusEnum.AI_ONGOING]: "green",
  [ThreadStatusEnum.NEW]: "red",
  [ThreadStatusEnum.OPEN]: "yellow",
  [ThreadStatusEnum.PENDING_USER_REPLY]: "yellow",
  [ThreadStatusEnum.ONHOLD]: "gray",
  [ThreadStatusEnum.PENDING_FORM_SUBMISSION]: "red",
  [ThreadStatusEnum.USER_CLOSED]: "green",
  [ThreadStatusEnum.SUPPORT_CLOSED]: "green",
  [ThreadStatusEnum.AUTO_CLOSED]: "green",
  [ThreadStatusEnum.SOFT_ALERT]: "yellow",
  [ThreadStatusEnum.AI_CLOSED]: "green",
  [ThreadStatusEnum.ESCALATED]: "red",
  [ThreadStatusEnum.REOPENED]: "red",
  [ThreadStatusEnum.UNKNOWN]: "blue",
});

export const threadStatusIconHollowMap = Object.freeze({
  [ThreadStatusEnum.PENDING_USER_REPLY]: true,
  [ThreadStatusEnum.PENDING_FORM_SUBMISSION]: true,
  // This is bad... I should separate but only one enum value so leave in here
  [EndUserTicketCategoryStatusEnum.AWAITING_YOUR_REPLY]: true,
});

const ticketStatusDropdownOptions = Object.freeze({
  [ThreadStatusEnum.AI_ONGOING]: [
    { text: "Raise Ticket", value: ThreadStatusEnum.NEW },
  ],
  [ThreadStatusEnum.NEW]: [
    { text: "Set to Open", value: ThreadStatusEnum.OPEN },
    { text: "Set to Pending User Reply", value: ThreadStatusEnum.PENDING_USER_REPLY },
    { text: "Set to On Hold", value: ThreadStatusEnum.ONHOLD },
    { text: "Close Ticket", value: ThreadStatusEnum.SUPPORT_CLOSED },
  ],
  [ThreadStatusEnum.OPEN]: [
    { text: "Set to Pending User Reply", value: ThreadStatusEnum.PENDING_USER_REPLY },
    { text: "Set to On Hold", value: ThreadStatusEnum.ONHOLD },
    { text: "Close Ticket", value: ThreadStatusEnum.SUPPORT_CLOSED },
  ],
  [ThreadStatusEnum.PENDING_USER_REPLY]: [
    { text: "Set to Open", value: ThreadStatusEnum.OPEN },
    { text: "Set to On Hold", value: ThreadStatusEnum.ONHOLD },
    { text: "Close Ticket", value: ThreadStatusEnum.SUPPORT_CLOSED },
  ],
  [ThreadStatusEnum.ONHOLD]: [
    { text: "Set to Open", value: ThreadStatusEnum.OPEN },
    { text: "Set to Pending User Reply", value: ThreadStatusEnum.PENDING_USER_REPLY },
    { text: "Close Ticket", value: ThreadStatusEnum.SUPPORT_CLOSED },
  ],
  [ThreadStatusEnum.PENDING_FORM_SUBMISSION]: [
    { text: "Set to Open", value: ThreadStatusEnum.OPEN },
    { text: "Close Ticket", value: ThreadStatusEnum.SUPPORT_CLOSED },
  ],
  [ThreadStatusEnum.USER_CLOSED]: [
    { text: "Reopen Ticket", value: ThreadStatusEnum.REOPENED },
  ],
  [ThreadStatusEnum.SUPPORT_CLOSED]: [
    { text: "Reopen Ticket", value: ThreadStatusEnum.REOPENED },
  ],
  [ThreadStatusEnum.AUTO_CLOSED]: [
    { text: "Reopen Ticket", value: ThreadStatusEnum.REOPENED },
  ],
  [ThreadStatusEnum.SOFT_ALERT]: [
    { text: "Raise Ticket", value: ThreadStatusEnum.NEW },
    { text: "AI Closed", value: ThreadStatusEnum.AI_CLOSED },
  ],
  [ThreadStatusEnum.AI_CLOSED]: [
    { text: "Raise Ticket", value: ThreadStatusEnum.NEW },
  ],
  [ThreadStatusEnum.ESCALATED]: [
    { text: "Set to Open", value: ThreadStatusEnum.OPEN },
    { text: "Set to Pending User Reply", value: ThreadStatusEnum.PENDING_USER_REPLY },
    { text: "Set to On Hold", value: ThreadStatusEnum.ONHOLD },
    { text: "Close Ticket", value: ThreadStatusEnum.SUPPORT_CLOSED },
  ],
  [ThreadStatusEnum.REOPENED]: [
    { text: "Set to Open", value: ThreadStatusEnum.OPEN },
    { text: "Set to Pending User Reply", value: ThreadStatusEnum.PENDING_USER_REPLY },
    { text: "Set to On Hold", value: ThreadStatusEnum.ONHOLD },
    { text: "Close Ticket", value: ThreadStatusEnum.SUPPORT_CLOSED },
  ],
});
</script>

<script setup>
const props = defineProps({
  status: {
    type: String,
    default: null,
  },
  allowToOpen: {
    type: Boolean,
    default: true,
  },
  paddingSize: {
    type: String,
    default: "normal",
  },
  threadDetails: {
    type: Object,
    default: {},
  },
  /** When true, selecting "Close Ticket" opens the close-ticket modal instead of emitting update-status */
  useCloseTicketModal: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update-status", "convert-to-ticket", "request-close-ticket"]);

const dropdownRef = ref();
const selected = ref(props.status);
const isModalVisible = ref(false);

watch(
  () => props.status,
  (val) => {
    selected.value = val;
  }
);

const onStatusClick = (option) => {
  console.log("option.value: ", option.value);
  const ticketStatuesArray = Object.values(TicketCategoryStatusEnum);

  // when useCloseTicketModal is true and user selects "Close Ticket", open the close-ticket modal
  if (props.useCloseTicketModal && option.value === ThreadStatusEnum.SUPPORT_CLOSED) {
    emit("request-close-ticket");
    isModalVisible.value = false;
    return;
  }

  // if converting non-ticket status to ticket status, emit convert-to-ticket event
  if (
    !ticketStatuesArray.includes(props.status) &&
    ticketStatuesArray.includes(option.value)
  ) {
    emit("convert-to-ticket", option.value);
  } else {
    emit("update-status", option.value);
  }

  isModalVisible.value = false;
};

const dropdownOptions = computed(
  () => ticketStatusDropdownOptions[selected.value] || []
);
const theme = computed(() => threadStatusThemeMap[selected.value] || null);

const isIconHollow = computed(() => threadStatusIconHollowMap[selected.value] || false)
</script>

<template>
  <dropdown
    ref="dropdownRef"
    class="status-dropdown"
    :class="{
      [theme]: theme,
      'is-visible': isModalVisible,
    }"
    :label="selected"
    header="Status"
    :show-header="false"
    v-model="selected"
    :options="dropdownOptions"
    :allowToOpen="allowToOpen"
    :padding-size="paddingSize"
    :showSearch="false"
    v-model:visibility="isModalVisible"
    :sort-options="false"
    v-b-tooltip="{ title: selected, trigger: 'hover' }"
    >
    <template #pre-label-icon>
      <IconCircleFill class="label-icon" :isHollow="isIconHollow" />
    </template>

    <div v-for="option in dropdownRef?.sortedOptions">
      <div
        class="dropdown-item d-flex align-items-center"
        :class="{ active: selected === option.value }"
        @click.stop="onStatusClick(option)"
      >
        <IconCircleFill :class="threadStatusThemeMap[option.value]" :isHollow="threadStatusIconHollowMap[option.value]" />
        <div
          class="d-flex align-items-center justify-content-between flex-grow-1"
        >
          <span class="option-label">{{ option.text }}</span>
          <IconCheck v-if="selected === option.value" />
        </div>
      </div>
    </div>
  </dropdown>
</template>

<style lang="scss" scoped>
.status-dropdown {
  stroke-width: 3px;

  :deep(.dropdown-menu) {
    width: 250px;

    .dropdown-item {
      .circle-icon {
        &.red {
          stroke: $red-200;
          fill: $red-200;
        }

        &.green {
          stroke: $green-150;
          fill: $green-150;
        }

        &.yellow {
          stroke: $yellow-250;
          fill: $yellow-250;
        }

        &.blue {
          stroke: var(--primary-250);
          fill: var(--primary-250);
        }

        &.gray {
          stroke: $neutral-250;
          fill: $neutral-250;
        }
      }
    }
  }

  .option-label {
    margin-left: 0.75rem;
  }

  &.is-visible {
    &.red {
      :deep(.btn) {
        background-color: $red-50;
        border: 1px solid $red-100;
      }
    }

    &.gray {
      :deep(.btn) {
        background-color: $neutral-150;
        border: 1px solid $neutral-300;
      }
    }

    &.green {
      :deep(.btn) {
        background-color: $neutral-150;
        border: 1px solid $neutral-300;
      }
    }

    &.yellow {
      :deep(.btn) {
        background-color: $yellow-50;
        border: 1px solid $yellow-150;
      }
    }

    &.blue {
      :deep(.btn) {
        background-color: var(--primary-50);
        border: 1px solid var(--primary-150);
      }
    }
  }

  &.red {
    :deep(.btn) {
      background-color: $red-0;
      border: 1px solid $red-50;

      &:hover {
        border: 1px solid $red-100;
      }
      &.show {
        background-color: $red-50;
        border: 1px solid $red-150;
      }

      .label-icon {
        stroke: $red-200;
        fill: $red-200;
      }
    }
  }

  &.green {
    :deep(.btn) {
      background-color: $green-0;
      border: 1px solid $green-50;

      &:hover {
        border: 1px solid $green-100;
      }
      &.show {
        background-color: $green-50;
        border: 1px solid $green-150;
      }
      .label-icon {
        stroke: $green-150;
        fill: $green-150;
      }
    }
  }

  &.yellow {
    :deep(.btn) {
      background-color: $yellow-0;
      border: 1px solid $yellow-50;
      &:hover {
        border: 1px solid $yellow-100;
      }
      &.show {
        background-color: $yellow-50;
        border: 1px solid $yellow-150;
      }
      .label-icon {
        stroke: $yellow-250;
        fill: $yellow-250;
      }
    }
  }

  &.blue {
    :deep(.btn) {
      background-color: var(--primary-0);
      border: 1px solid var(--primary-50);
      &:hover {
        border: 1px solid var(--primary-100);
      }
      &.show {
        background-color: var(--primary-50);
        border: 1px solid var(--primary-150);
      }
      .label-icon {
        stroke: var(--primary-250);
        fill: var(--primary-250);
      }
    }
  }

  &.gray {
    :deep(.btn) {
      background-color: $neutral-50;
      border: 1px solid $neutral-150;
      &:hover {
        border: 1px solid $neutral-150;
      }
      &.show {
        background-color: $neutral-100;
        border: 1px solid $neutral-250;
      }
      .label-icon {
        stroke: $neutral-250;
        fill: $neutral-250;
      }
    }
  }

  &.active {
    background: var(--primary-0);
    border: 1px solid var(--primary-50);
  }
}
</style>
